import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auraApi } from "../../lib/aura";
import { AvatarMedia } from "./AuraFrameless";
import AuraDockText from "./AuraDockText";
import { useAura } from "./AuraProvider";

// Docket-style Simmi, docked at the bottom-right of every page and fixed while the visitor scrolls: just the
// avatar and her "Talk to Me" button until the visitor starts; then her speech card and controls float to
// her left. Ending the conversation simply hides her (a small "Talk to Simmi" button stays in the corner).

const AuraDock = () => {
  const {
    config,
    callActive,
    callSurface,
    callState,
    avatarMode,
    textVoice,
    messages,
    startCall,
    canTalk,
    open,
    setOpen,
    hidden,
    setHidden,
    outputMuted,
    prefetchAvatar,
  } = useAura();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    auraApi.avatarPreview().then(setPreview).catch(() => {});
  }, []);

  const talk = () => {
    setHidden(false);
    startCall("dock");
  };

  // Handle global open-aura-agent event to trigger Simmi conversation immediately
  useEffect(() => {
    const handleOpenAgent = () => {
      setHidden(false);
      setOpen(true);
      startCall("dock");
    };
    window.addEventListener("open-aura-agent", handleOpenAgent);
    return () => window.removeEventListener("open-aura-agent", handleOpenAgent);
  }, [setHidden, setOpen, startCall]);

  const goTo = (path) => {
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  const live = callActive && callSurface === "dock";
  const textMode = open && !live; // typed conversation, shown in the dock (no chat window)
  const last = [...messages].reverse().find((m) => m.role === "assistant");
  const endedId = !live && last?.kind === "end" ? last.id : null;

  // Simmi closed the session herself after a long silence: once she has said goodbye, hide her.
  useEffect(() => {
    if (endedId) {
      setOpen(false);
      setHidden(true);
    }
  }, [endedId, setOpen, setHidden]);

  const mode = live ? "call" : textMode ? "text" : null;

  if (hidden && !mode) {
    return (
      <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-3 sm:bottom-6 sm:right-6">
        <button
          onClick={talk} /* one click: Simmi reappears and the conversation starts */
          onMouseEnter={prefetchAvatar}
          onFocus={prefetchAvatar}
          aria-label="Talk to Simmi"
          className="aura-glow-btn group"
        >
          <span className="aura-glow-btn-inner">
            {preview?.portrait_image_url ? (
              <img
                src={preview.portrait_image_url}
                alt="Simmi"
                className="h-7 w-7 rounded-full object-cover object-top border border-purple-400/40"
              />
            ) : (
              <span className="aura-orb h-7 w-7 rounded-full" />
            )}
            Talk to Simmi
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-3 right-3 z-[60] flex flex-col items-end gap-3 sm:bottom-0 sm:right-4 sm:flex-row sm:items-end sm:gap-1"
      role="region"
      aria-label="Simmi, Simpel's AI agent"
    >
      {mode && <AuraDockText key={mode} mode={mode} onNavigate={goTo} />}

      {/* The avatar stage with its backlight — like Docket's, the resting avatar is clickable (pointer cursor)
          and hovering her makes the "Talk to Me" ring glow brighter. Nothing changes mid-conversation. */}
      <div
        className={`aura-stage relative h-[260px] w-[160px] sm:h-[420px] sm:w-[240px] ${
          !mode && canTalk ? "aura-stage--clickable" : ""
        }`}
        onClick={!mode && canTalk ? talk : undefined}
        onMouseEnter={() => {
          setHovered(true);
          if (!mode && canTalk) prefetchAvatar(); // likely about to talk: get the session ready now
        }}
        onTouchStart={!mode && canTalk ? prefetchAvatar : undefined}
        onMouseLeave={() => setHovered(false)}
      >
        <AvatarMedia
          preview={preview}
          keyed={Boolean(config.avatar_greenscreen)}
          live={avatarMode && (live || textMode)}
          liveVisible={live ? callState !== "idle" : textVoice === "on"}
          muted={outputMuted}
          sheen={hovered && !mode && canTalk} /* white diagonal sweep on hover, only before the conversation */
        />
        {/* Docket's "Talk to Me": bottom-right of the avatar, gone once the conversation starts */}
        {!mode && canTalk && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // the stage behind it starts the call too; don't start it twice
              talk();
            }}
            className="aura-talk-me"
          >
            Talk to Me
          </button>
        )}
      </div>
    </div>
  );
};

export default AuraDock;
